<template>
  <FullPageLayout>
    <template>
      <div class="mx-auto w-full sm:w-[1100px]">
        <div class="px-4 sm:px-0 sm:mb-16">
          <div class="flex items-center mt-6 justify-between">
            <OBreadcrumb class="capitalize text-md leading-18" :items="breadcrumbRoutes" show-home-page-title />
            <FavoriteArticlesButton v-if="isMobile && isLoggedIn" />
          </div>

          <div v-if="!isMobile" class="u-center flex-col">
            <h2 class="text-[#121212] text-[44px] font-medium">
              Onedio'ya
              <span class="px-4 py-2 kesfetBorder">keşfet</span>
              geldi!
            </h2>
            <h3 class="text-black self-start sm:self-center leading-normal tracking-tight font-normal text-20 sm:text-24 mt-3">
              İlginizi çeken kategorileri seçin,
              <br v-if="isMobile" />
              sadece takip ettiğiniz içerikleri görün.
            </h3>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row items-start justify-center w-full sm:w-[1100px]">
          <KesfetMenu ref="kesfetMenuRef" @getArticles="getArticlesMenu">
            <KesfetCategoryFilter v-if="showCategoryFilter && !isMobile" @handleCategoryChange="handleCategoryChange" />
          </KesfetMenu>
          <KesfetCategoryFilter v-if="showCategoryFilter && isMobile" class="mx-auto" @handleCategoryChange="handleCategoryChange" />

          <div v-if="isMobile" class="border-t border-[#000]/[.1] w-full my-4"></div>

          <OBackButton v-if="isMobile" />

          <div class="flex flex-col mx-auto" :class="isMobile && 'w-full px-4'">
            <OLoading v-if="loading && filterchanged" class="mx-auto" />
            <div v-if="!filterchanged">
              <KesfetArticleCard
                v-for="(article, index) in articles"
                :key="article._id"
                :data="article"
                class="mb-4"
                :data-layer="{ name: `Keşfet`, position: index + 1 }"
                @selectArticle="selectArticle()"
              />
            </div>

            <OLoading v-if="hasNextPage" ref="infiniteScrollRef" class="mt-6 u-center" />
          </div>
          <ArticleShareModalMobile v-if="isMobile && isArticleShareModalOpen" :article="selectedArticle" />
          <ArticleReactionModalMobile v-if="isMobile && isArticleReactionsModalOpen" :article="selectedArticle" />
          <aside v-if="!isMobile" class="flex flex-col sm:ml-4 sm:mr-0 mx-auto px-4 sm:px-0 sm:w-[300px]">
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <DailyEmojile cancel-sticky is-sidebar class="mx-auto" />
            </LazyHydrate>
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <ReadingListSidebar class="w-full h-full bg-white mb-4" />
            </LazyHydrate>
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <DailyBilpop cancel-sticky is-sidebar class="mx-auto" />
            </LazyHydrate>
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <CurrencySidebar :currencies="currencies" class="w-full h-full bg-white mb-4" />
            </LazyHydrate>
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <HoroscopeSidebar class="w-full h-full bg-white mb-4" />
            </LazyHydrate>
            <LazyHydrate :when-visible="{ rootMargin: '100px' }">
              <NewsSidebar class="w-full h-full bg-white mb-4" />
            </LazyHydrate>
          </aside>
        </div>
      </div>
    </template>
  </FullPageLayout>
</template>

<script>
import { defineComponent, ref, useRoute, useContext, computed, provide, onMounted } from '@nuxtjs/composition-api';
import FullPageLayout from '@/modules/common/components/layout/FullPageLayout';
import OBreadcrumb from '@/modules/common/components/ui/OBreadcrumb/OBreadcrumb.vue';
import { useDevice } from '@/composables/useDevice';
import { useInfiniteScroll } from '@/composables/useInfiniteScroll';
import LazyHydrate from 'vue-lazy-hydration';
import { useTracking } from '@/modules/tracking/composables/useTracking';
import OBackButton from '@/modules/common/components/ui/OBackButton/OBackButton';
import { useAuth } from '~/modules/auth/state/useAuth';
import OLoading from '~/modules/common/components/ui/OLoading/OLoading';
import ListArticleCard from '~/modules/card/templates/common/ListArticleCard';
import KesfetMenu from '~/modules/kesfet/KesfetMenu.vue';
import KesfetArticleCard from '~/modules/card/templates/kesfet/KesfetArticleCard.vue';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import KesfetCategoryFilter from '~/modules/kesfet/KesfetCategoryFilter.vue';
import { currencies } from '~/modules/kesfet/constants/currencies';
import FavoriteArticlesButton from '~/modules/kesfet/components/FavoriteArticlesButton.vue';
import { useKesfetMeta } from '~/modules/kesfet/utils/kesfetMeta';
import { eventBus } from '~/modules/common/utils/eventBus';
import ArticleReactionModalMobile from '~/modules/card/templates/article/ArticleReactionModalMobile.vue';
import ArticleShareModalMobile from '~/modules/card/templates/article/ArticleShareModalMobile.vue';

export default defineComponent({
  head: {
    bodyAttrs: {
      class: 'bg-[#f7f8fb] o-ps-global',
    },
  },
  components: {
    FullPageLayout,
    OLoading,
    OBreadcrumb,
    ListArticleCard,
    DailyEmojile: () => import(/* webpackChunkName: "DailyEmojile" */ '~/modules/oyun/components/previousGames/DailyEmojile.vue'),
    CurrencySidebar: () => import(/* webpackChunkName: "CurrencySidebar" */ '~/modules/kesfet/components/CurrencySidebar.vue'),
    HoroscopeSidebar: () => import(/* webpackChunkName: "HoroscopeSidebar" */ '~/modules/kesfet/components/HoroscopeSidebar.vue'),
    NewsSidebar: () => import(/* webpackChunkName: "NewsSidebar" */ '~/modules/kesfet/components/NewsSidebar.vue'),
    ReadingListSidebar: () => import(/* webpackChunkName: "ReadingListSidebar" */ '~/modules/kesfet/components/ReadingListSidebar.vue'),
    KesfetMenu,
    DailyBilpop: () => import(/* webpackChunkName: "DailyBilpop" */ '~/modules/oyun/components/previousGames/DailyBilpop.vue'),
    KesfetArticleCard,
    LazyHydrate,
    KesfetCategoryFilter,
    FavoriteArticlesButton,
    ArticleReactionModalMobile,
    ArticleShareModalMobile,
    OBackButton,
  },
  setup() {
    const route = useRoute();
    const { isMobile } = useDevice();
    const { $service, $error, $cache } = useContext();
    const { user, isLoggedIn } = useAuth();
    const breadcrumbRoutes = [{ href: route.value.fullPath, name: 'Keşfet' }];
    const articles = ref([]);
    const selectedMenuType = ref();
    const page = ref(0);
    const hasNextPage = ref(false);
    const kesfetMenuRef = ref();
    const loading = ref(false);
    const isArticleReactionsModalOpen = ref();
    const isArticleShareModalOpen = ref();
    const selectedArticle = ref();

    const menuNames = { trend: 'Trendler', 'sana-ozel': 'Sana Özel', 'en-son': 'En Son İçerikler' };
    const menuType = ref();
    const dataLayerName = computed(() =>
      menuType.value === 'takip' ? 'Takip Ettiklerim - Filtre' : 'Keşfet - ' + menuNames[menuType.value]
    );

    provide('dataLayerName', dataLayerName);

    onMounted(() => {
      menuType.value = localStore.getItem('o-main-filter');
    });

    eventBus.on('isArticleShareModalOpen', (article) => {
      isArticleShareModalOpen.value = true;
      selectedArticle.value = article;
    });

    eventBus.on('isArticleShareModalClosed', () => {
      isArticleShareModalOpen.value = false;
      selectedArticle.value = null;
    });

    eventBus.on('isArticleReactionsModalOpen', (article) => {
      isArticleReactionsModalOpen.value = true;
      selectedArticle.value = article;
    });

    eventBus.on('isArticleReactionsModalClosed', () => {
      isArticleReactionsModalOpen.value = false;
      selectedArticle.value = null;
    });

    const lastCategory = () => {
      const localData = localStore.getItem('o-followed-categories');
      if (localData?.length) return localData[localData.length - 1].name;
      return 'Tümü';
    };

    provide('lastCategory', { lastCategory });

    useKesfetMeta();

    $cache('public, max-age=30');
    const showCategoryFilter = computed(() => kesfetMenuRef.value?.selectedMainFilter === 'takip');
    const handleCategoryChange = () => {
      page.value = 1;
      hasNextPage.value = false;
      getDiscover();
    };

    const filterchanged = ref(false);
    const getArticlesMenu = async (menuType) => {
      filterchanged.value = true;
      await getArticles(menuType);
    };

    const getArticles = async (menuType) => {
      loading.value = true;
      if (selectedMenuType.value !== menuType) {
        page.value = 0;
        hasNextPage.value = false;
      }
      selectedMenuType.value = menuType;
      page.value++;
      if (menuType === 'takip') {
        await getDiscover();
      } else if (menuType === 'trend') {
        await getTrends();
      } else if (menuType === 'sana-ozel') {
        await getRecommendedArticle();
      } else {
        await getLatest();
      }
      loading.value = false;
      filterchanged.value = false;
    };

    const getDiscover = async () => {
      try {
        const categoryIds = kesfetMenuRef.value?.selectedCategories?.length
          ? { categoryIds: kesfetMenuRef.value.selectedCategories.map((a) => a.id).join() }
          : localStore.getItem('o-followed-categories')?.length
          ? {
              categoryIds: localStore
                .getItem('o-followed-categories')
                .map((a) => a.id)
                .join(),
            }
          : {};

        const { data, pagination } = await $service.discover.getDiscover({
          page: page.value,
          reactions: true,
          comments: true,
          hashtags: true,
          categories: true,
          ...categoryIds,
        });
        hasNextPage.value = pagination.hasNextPage;
        if (page.value > 1) {
          articles.value = articles.value.concat(data);
        } else articles.value = data;
      } catch (error) {
        $error(error);
      }
    };

    const getTrends = async () => {
      try {
        const { data, pagination } = await $service.article.getTrends({
          page: page.value,
          reactions: true,
          comments: true,
          description: true,
          favoriteCheck: true,
        });
        hasNextPage.value = pagination.hasNextPage;
        if (page.value > 1) {
          articles.value = articles.value.concat(data);
        } else articles.value = data;
      } catch (error) {
        $error(error);
      }
    };
    const getRecommendedArticle = async () => {
      try {
        const anonymUserId = localStorage.getItem('o-oet-ti');
        const userId = isLoggedIn.value ? user.value?._id : JSON.parse(anonymUserId)?.data;
        const { data: recommendedArticles, pagination } = await $service.category.getRecommendedArticles({
          page: page.value,
          userId,
          placementId: 1,
        });
        hasNextPage.value = pagination.hasNextPage;
        if (page.value > 1) {
          articles.value = articles.value.concat(recommendedArticles);
        } else articles.value = recommendedArticles;
      } catch (error) {
        $error(error);
      }
    };
    const getLatest = async () => {
      try {
        const { data, pagination } = await $service.article.getAllArticles({
          page: page.value,
          comments: true,
          favoriteCheck: true,
          reactions: true,
        });
        hasNextPage.value = pagination.hasNextPage;
        if (page.value > 1) {
          articles.value = articles.value.concat(data);
        } else articles.value = data;
      } catch (error) {
        $error(error);
      }
    };
    const [infiniteScrollRef] = useInfiniteScroll(
      async () => {
        await getArticles(selectedMenuType.value);
      },
      { rootMargin: '300px' }
    );

    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent({
      page: {
        name: 'Keşfet',
        type: 'Keşfet',
      },
    });
    // ------- Google Pageview Event End -------

    return {
      breadcrumbRoutes,
      articles,
      currencies,
      isMobile,
      getArticlesMenu,
      infiniteScrollRef,
      hasNextPage,
      kesfetMenuRef,
      showCategoryFilter,
      handleCategoryChange,
      loading,
      isLoggedIn,
      isArticleReactionsModalOpen,
      selectedArticle,
      isArticleShareModalOpen,
      page,
      filterchanged,
    };
  },
});
</script>
<style lang="scss" scoped>
.kesfetBorder {
  border-radius: 12px;
  background-image: linear-gradient(to right, #306aff, #cc4ad4);
  background-origin: border-box;
  box-shadow: inset 0 100vw #f7f8fb;
  border: 3px solid transparent;
}
</style>
